import React from "react";
import PropTypes from "prop-types";

/** Canonical 0–10 pain intensity levels for all clinical forms. */
export const PAIN_LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Compact 0–10 pain scale — full-width segmented squares (no gaps between boxes).
 * Shared by Nurse, PT, OT, and ST clinical forms via PainScalePills re-export.
 */
const PainIntensityScale = ({
  label = "Pain Intensity Scale (0–10)",
  value,
  canEdit = false,
  onChange,
  name,
  className = "",
  showEndpointLabels = true,
}) => (
  <div
    className={`rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-2 sm:px-3 ${className}`}
  >
    {label ? (
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
    ) : null}

    {showEndpointLabels ? (
      <div className="mb-1 flex items-center justify-between gap-2 px-0.5">
        <span className="text-[10px] font-medium text-slate-400">No Pain</span>
        <span className="text-[10px] font-semibold text-rose-600">Severe</span>
      </div>
    ) : null}

    <div
      className="flex w-full overflow-hidden rounded-md border border-indigo-200 bg-white shadow-sm"
      role="radiogroup"
      aria-label={label || name || "Pain intensity 0 to 10"}
    >
      {PAIN_LEVELS.map((level, index) => {
        const active = value === level;
        const isLast = index === PAIN_LEVELS.length - 1;

        return (
          <button
            key={level}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`Pain level ${level}`}
            disabled={!canEdit}
            onClick={() => onChange?.(active ? null : level)}
            className={`relative flex-1 min-w-0 h-6 sm:h-7 text-[10px] sm:text-[11px] font-semibold tabular-nums transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              !isLast ? "border-r border-indigo-200" : ""
            } ${
              active
                ? "z-[1] bg-indigo-600 text-white"
                : "bg-white text-indigo-800 hover:bg-indigo-50"
            }`}
          >
            {level}
          </button>
        );
      })}
    </div>
  </div>
);

PainIntensityScale.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  className: PropTypes.string,
  showEndpointLabels: PropTypes.bool,
};

export default PainIntensityScale;
